import { formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useGetVideoCommentsReceivedByRbtRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface CommentDistributionRawDataProps {
  isCommentDistributionWithCommentType: boolean;
}
type CommentDistributionRawDataType = {
  clientId: number;
  clientName: string;
  sessionDate: string;
  sessionDatetime: string;
  sessionDuration: number;
  totalComments: number;
  rbtName: string;
  commentTypeCounts: {
    love: number;
    praise: number;
    question: number;
    feedback: number;
    totalComments: number;
  };
};
export const CommentDistributionRawData: React.FC<CommentDistributionRawDataProps> = ({
  isCommentDistributionWithCommentType,
}) => {
  const { selectedRBTDates, selectedRBTs } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedRBTs);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT]);

  const { data, isFetching } = useGetVideoCommentsReceivedByRbtRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["getVideoCommentsReceivedByRbtRawData", dates, selectedRBT, pageNo, pageSize],
    },
  );

  const columns: Column<CommentDistributionRawDataType>[] = [
    {
      title: "Client ID",
      key: "clientId",
      dataIndex: "clientId",
    },
    {
      title: "Client",
      key: "clientName",
      dataIndex: "clientName",
      render: (_, record) => <span className="ph-no-capture">{record.clientName || "-"}</span>,
    },
    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Session Duration",
      key: "sessionDuration",
      dataIndex: "sessionDuration",
      render: (value) => {
        return value ? formatSecondsToHHMMSS(value as number) : "-";
      },
    },
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (_, record) => record.rbtName || "-",
    },
    {
      title: "Love",
      key: "love",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.love ?? 0,
    },
    {
      title: "Praise",
      key: "praise",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.praise ?? 0,
    },
    {
      title: "Feedback",
      key: "feedback",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.feedback ?? 0,
    },
    {
      title: "Question",
      key: "question",
      dataIndex: "commentTypeCounts",
      render: (_, record) => record.commentTypeCounts?.question ?? 0,
    },
    {
      title: "Total Comments",
      key: "totalComments",
      dataIndex: "totalComments",
    },
  ];

  const filteredColumn = isCommentDistributionWithCommentType
    ? columns
    : columns.filter((item) => item.dataIndex !== "commentTypeCounts");
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        showClientFilter={false}
        showBCBAFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.getVideoCommentsReceivedByRbtRawData?.rawData
            ? (data.getVideoCommentsReceivedByRbtRawData?.rawData as CommentDistributionRawDataType[])
            : []
        }
        columns={filteredColumn}
        loading={isFetching}
        totalItemCount={data?.getVideoCommentsReceivedByRbtRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
